import { useEffect, useState, type FormEvent } from 'react';
import { ArrowLeft, CalendarPlus, Users } from 'lucide-react';
import { errorMessage, useLocale } from '../../i18n';
import { ConfirmButton } from '../ui/ConfirmButton';
import {
  cancelLinkActivity,
  createLinkActivity,
  fetchLinkActivities,
  updateLinkActivity,
  type LinkActivitiesRead,
  type LinkActivity,
  type LinkActivityInput,
  type LinkActivityWrite,
} from '../../lib/api/linkActivities';

// ─────────────────────────────────────────────────────────────────────
// «AKTIVITETER HOS KLUBBEN» — a centre arranges activities in a club's name
// through a co-operation (slice 4d, 2026-10-21; the owner's §4k decisions).
//
// The page draws the server's answer and decides nothing:
//
//   1A  only the activities THIS centre arranged in the club are listed, and
//       only those can be changed or cancelled;
//   2A  who signed up, by name — never the club's member list;
//   3A  a page of its own, with what an activity needs and no more: name,
//       date, time, capacity, description;
//   4A  courts are the centre's to book as for its own activities — the page
//       says so, and books nothing.
//
// Every string is a catalog key; refusal codes become sentences through
// `errorMessage`.
// ─────────────────────────────────────────────────────────────────────

export interface LinkActivitiesViewProps {
  /** The club the activities are arranged in. */
  hostId: string;
  hostName: string;
  /** The centre this is on behalf of, when the account has several (`?home=`). */
  homeId: string | null;
  onBack: () => void;
  /** Injectable for tests. */
  load?: typeof fetchLinkActivities;
  create?: typeof createLinkActivity;
  update?: typeof updateLinkActivity;
  cancel?: typeof cancelLinkActivity;
}

type Notice = { kind: 'ok' | 'error'; text: string } | null;

interface Draft {
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  maxParticipants: string;
  description: string;
  kind: 'arrangement' | 'coaching';
}
const EMPTY: Draft = {
  name: '',
  date: '',
  startTime: '',
  endTime: '',
  maxParticipants: '',
  description: '',
  kind: 'arrangement',
};
const fromActivity = (a: LinkActivity): Draft => ({
  name: a.name,
  date: a.date ?? '',
  startTime: a.startTime ?? '',
  endTime: a.endTime ?? '',
  maxParticipants: a.maxParticipants === null ? '' : String(a.maxParticipants),
  description: a.description ?? '',
  kind: a.kind === 'coaching' ? 'coaching' : 'arrangement',
});

/** The draft as the server takes it, or the catalog key of what is wrong. */
function toInput(d: Draft):
  | LinkActivityInput
  | {
      invalid:
        | 'linkActivities.invalid.name'
        | 'linkActivities.invalid.date'
        | 'linkActivities.invalid.capacity';
    } {
  if (!d.name.trim()) return { invalid: 'linkActivities.invalid.name' };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.date)) return { invalid: 'linkActivities.invalid.date' };
  const max = d.maxParticipants.trim();
  if (max && !/^\d+$/.test(max)) return { invalid: 'linkActivities.invalid.capacity' };
  return {
    name: d.name.trim(),
    date: d.date,
    startTime: d.startTime || null,
    endTime: d.endTime || null,
    maxParticipants: max ? Number(max) : null,
    description: d.description.trim() || null,
    kind: d.kind,
  };
}

export function LinkActivitiesView({
  hostId,
  hostName,
  homeId,
  onBack,
  load = fetchLinkActivities,
  create = createLinkActivity,
  update = updateLinkActivity,
  cancel = cancelLinkActivity,
}: LinkActivitiesViewProps) {
  const { t } = useLocale();
  const [result, setResult] = useState<LinkActivitiesRead | null>(null);
  const [generation, setGeneration] = useState(0);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  /** The activity being edited, or null for a new one. */
  const [editing, setEditing] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void load(hostId, homeId).then((r) => {
      if (live) setResult(r);
    });
    return () => {
      live = false;
    };
  }, [hostId, homeId, generation, load]);
  const reload = () => setGeneration((n) => n + 1);

  const settle = (outcome: LinkActivityWrite, done: string) => {
    if (outcome.state === 'ok') {
      setNotice({ kind: 'ok', text: done });
      setDraft(EMPTY);
      setEditing(null);
    } else if (outcome.state === 'forbidden') {
      setNotice({ kind: 'error', text: t('linkActivities.forbidden') });
    } else if (outcome.state === 'refused') {
      setNotice({ kind: 'error', text: errorMessage(t, outcome.reason) });
    } else setNotice({ kind: 'error', text: t('errors.unknown') });
    reload();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const input = toInput(draft);
    if ('invalid' in input) {
      setNotice({ kind: 'error', text: t(input.invalid) });
      return;
    }
    setBusy(true);
    setNotice(null);
    settle(
      editing ? await update(hostId, editing, input, homeId) : await create(hostId, input, homeId),
      t(editing ? 'linkActivities.saved' : 'linkActivities.created', { name: input.name }),
    );
    setBusy(false);
  };

  const doCancel = async (a: LinkActivity) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    settle(await cancel(hostId, a.id, homeId), t('linkActivities.cancelled', { name: a.name }));
    setBusy(false);
  };

  const field = (key: keyof Draft, label: string, type: 'text' | 'date' | 'time' = 'text') => (
    <label className="block">
      <span className="font-mono uppercase tracking-wider text-xs text-ink-3">{label}</span>
      <input
        className="input mt-1 w-full"
        type={type}
        value={draft[key]}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
        data-testid={`link-activity-${key}`}
        disabled={busy}
      />
    </label>
  );

  const body = () => {
    if (result === null) {
      return (
        <p className="text-sm text-ink-3" data-testid="link-activities-loading">
          {t('linkActivities.loading')}
        </p>
      );
    }
    if (result.state === 'forbidden') {
      return (
        <p className="text-sm text-danger" data-testid="link-activities-forbidden">
          {t('linkActivities.forbidden')}
        </p>
      );
    }
    if (result.state === 'refused') {
      return (
        <p className="text-sm text-danger" data-testid="link-activities-refused">
          {errorMessage(t, result.reason)}
        </p>
      );
    }
    if (result.state !== 'ok') {
      return (
        <div data-testid="link-activities-unavailable">
          <p className="text-sm text-danger">{t('linkActivities.unavailable')}</p>
          <button type="button" onClick={reload} className="btn-outline text-xs mt-2">
            {t('linkActivities.retry')}
          </button>
        </div>
      );
    }
    const { activities } = result.value;
    return (
      <div className="space-y-4">
        <p className="text-xs text-ink-3" data-testid="link-activities-courts">
          {t('linkActivities.courts')}
        </p>
        {notice && (
          <p
            role="status"
            data-testid="link-activities-notice"
            className={`text-sm ${notice.kind === 'ok' ? 'text-ink-2' : 'text-danger'}`}
          >
            {notice.text}
          </p>
        )}

        <section className="card p-5" data-testid="link-activities-form">
          <h2 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2">
            <CalendarPlus size={18} />{' '}
            {editing ? t('linkActivities.edit.title') : t('linkActivities.new.title')}
          </h2>
          <form onSubmit={submit} className="mt-3 grid gap-3 sm:grid-cols-2">
            {field('name', t('linkActivities.field.name'))}
            <label className="block">
              <span className="font-mono uppercase tracking-wider text-xs text-ink-3">
                {t('linkActivities.field.kind')}
              </span>
              <select
                className="input mt-1 w-full"
                value={draft.kind}
                onChange={(e) => setDraft({ ...draft, kind: e.target.value as Draft['kind'] })}
                data-testid="link-activity-kind"
                disabled={busy}
              >
                <option value="arrangement">{t('linkActivities.kind.arrangement')}</option>
                <option value="coaching">{t('linkActivities.kind.coaching')}</option>
              </select>
            </label>
            {field('date', t('linkActivities.field.date'), 'date')}
            {field('maxParticipants', t('linkActivities.field.capacity'))}
            {field('startTime', t('linkActivities.field.start'), 'time')}
            {field('endTime', t('linkActivities.field.end'), 'time')}
            <label className="block sm:col-span-2">
              <span className="font-mono uppercase tracking-wider text-xs text-ink-3">
                {t('linkActivities.field.description')}
              </span>
              <textarea
                className="input mt-1 w-full"
                rows={3}
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                data-testid="link-activity-description"
                disabled={busy}
              />
            </label>
            <div className="sm:col-span-2 flex justify-end gap-2">
              {editing && (
                <button
                  type="button"
                  className="btn-outline text-xs"
                  onClick={() => {
                    setEditing(null);
                    setDraft(EMPTY);
                  }}
                  disabled={busy}
                >
                  {t('linkActivities.edit.stop')}
                </button>
              )}
              <button
                type="submit"
                className="btn-lime text-xs"
                disabled={busy}
                data-testid="link-activity-submit"
              >
                {editing ? t('linkActivities.save') : t('linkActivities.create')}
              </button>
            </div>
          </form>
        </section>

        {activities.length === 0 ? (
          <p className="text-sm text-ink-3" data-testid="link-activities-none">
            {t('linkActivities.none')}
          </p>
        ) : (
          <ul className="space-y-3" data-testid="link-activities-list">
            {activities.map((a) => (
              <li key={a.id} className="card p-5" data-testid={`link-activity-${a.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-display text-base font-bold text-ink">{a.name}</h3>
                  {a.status === 'cancelled' && (
                    <span className="rounded-full border border-line px-2 py-0.5 text-[11px] text-ink-3">
                      {t('linkActivities.status.cancelled')}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-ink-3">
                  {a.date ?? ''} {a.startTime ?? ''}
                  {a.endTime ? `–${a.endTime}` : ''}
                </p>
                <div
                  className="mt-3 text-sm text-ink"
                  data-testid={`link-activity-signups-${a.id}`}
                >
                  <span className="inline-flex items-center gap-1 text-xs text-ink-3">
                    <Users size={12} />{' '}
                    {a.maxParticipants === null
                      ? t('linkActivities.signups', { count: String(a.signups.length) })
                      : t('linkActivities.signupsOf', {
                          count: String(a.signups.length),
                          max: String(a.maxParticipants),
                        })}
                  </span>
                  {a.signups.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {a.signups.map((s, i) => (
                        <li key={i} className="text-sm">
                          {s.partner ? `${s.name} & ${s.partner}` : s.name}
                          {s.status === 'waitlist' && (
                            <span className="ml-2 text-xs text-ink-3">
                              {t('linkActivities.waitlist')}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {a.status !== 'cancelled' && (
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      type="button"
                      className="btn-outline text-xs"
                      onClick={() => {
                        setEditing(a.id);
                        setDraft(fromActivity(a));
                      }}
                      disabled={busy}
                      data-testid={`link-activity-edit-${a.id}`}
                    >
                      {t('linkActivities.edit')}
                    </button>
                    <ConfirmButton
                      question={t('linkActivities.cancel.question', { name: a.name })}
                      confirmLabel={t('linkActivities.cancel.confirm')}
                      onConfirm={() => void doCancel(a)}
                      className="btn-outline text-xs"
                      disabled={busy}
                    >
                      {t('linkActivities.cancel')}
                    </ConfirmButton>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const v = result?.state === 'ok' ? result.value : null;
  return (
    <div
      className="max-w-2xl mx-auto px-4 md:px-6 py-10 slide-up"
      data-testid="link-activities-view"
    >
      <button
        type="button"
        onClick={onBack}
        className="text-xs font-mono text-ink-3 hover:text-ink-2 mb-2 flex items-center gap-1"
        data-testid="link-activities-back"
      >
        <ArrowLeft size={12} /> {t('linkActivities.back')}
      </button>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold mb-1">
          {t('linkActivities.title', { club: v?.host.name ?? hostName })}
        </h1>
        <p className="text-sm text-ink-3">
          {v ? t('linkActivities.lead', { centre: v.home.name }) : t('linkActivities.loading')}
        </p>
      </div>
      {body()}
    </div>
  );
}
